import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import axios from "axios";
import {
  Search,
  Building2,
  ArrowRight,
  Loader2,
  ArrowLeft,
  Shield,
  AlertCircle,
  CheckCircle,
  RefreshCw,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const FindCompany = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState("");
  
  // Estado para empresa recordada (acceso rápido PWA)
  const [rememberedCompany, setRememberedCompany] = useState(null);
  const [showQuickAccess, setShowQuickAccess] = useState(false);

  // Verificar si hay empresa guardada al cargar
  useEffect(() => {
    const savedSlug = localStorage.getItem('rememberedCompanySlug');
    const savedName = localStorage.getItem('rememberedCompanyName');
    const savedLogo = localStorage.getItem('rememberedCompanyLogo');
    
    if (savedSlug && savedName) {
      setRememberedCompany({
        slug: savedSlug,
        name: savedName,
        logo: savedLogo
      });
      setShowQuickAccess(true);
    }
  }, []);

  const handleQuickAccess = () => {
    if (rememberedCompany?.slug) {
      navigate(`/empresa/${rememberedCompany.slug}/login`);
    }
  };

  const handleChangeCompany = () => {
    setShowQuickAccess(false);
    // No borramos localStorage aquí, solo mostramos la búsqueda
  };

  const handleForgetCompany = () => {
    localStorage.removeItem('rememberedCompanySlug');
    localStorage.removeItem('rememberedCompanyName');
    localStorage.removeItem('rememberedCompanyLogo');
    setRememberedCompany(null);
    setShowQuickAccess(false);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    
    const term = searchTerm.trim().toLowerCase();
    
    // Si escribe "superadmin" o "super admin", ir al portal de Super Admin
    if (term === "superadmin" || term === "super admin" || term === "admin") {
      navigate("/admin-portal");
      return;
    }
    
    if (!term || term.length < 2) {
      setError("Ingresa al menos 2 caracteres para buscar");
      return;
    }
    
    setLoading(true);
    setError("");
    setSearchResults([]);
    
    try {
      const response = await axios.get(`${API_URL}/api/public/find-company`, {
        params: { q: term }
      });
      
      setSearchResults(response.data.companies || []);
      setSearched(true);
      
      if (response.data.companies?.length === 0) {
        setError("No se encontró ninguna empresa con ese nombre o correo");
      }
    } catch (err) {
      console.error("Search error:", err);
      setError("Error al buscar. Intenta de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = (company) => {
    navigate(`/empresa/${company.slug}/login`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_cia-operacional/artifacts/3tb1ht5b_Logo%20CIA%20PNG.png"
              alt="CIA Servicios®"
              className="h-16 w-auto mx-auto"
            />
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">
            {showQuickAccess ? "Acceso Rápido" : "Iniciar Sesión"}
          </h1>
          <p className="text-blue-200">
            {showQuickAccess ? "Continúa donde lo dejaste" : "Busca tu empresa para acceder al sistema"}
          </p>
        </div>

        {/* Quick Access Card (si hay empresa recordada) */}
        {showQuickAccess && rememberedCompany && (
          <Card className="shadow-2xl border-0 mb-4">
            <CardContent className="p-8">
              <div className="text-center">
                {/* Logo de la empresa o ícono por defecto */}
                <div className="mx-auto w-20 h-20 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-2xl flex items-center justify-center mb-4 overflow-hidden">
                  {rememberedCompany.logo ? (
                    <img 
                      src={rememberedCompany.logo.startsWith('data:') ? rememberedCompany.logo : `data:image/png;base64,${rememberedCompany.logo}`}
                      alt={rememberedCompany.name}
                      className="w-full h-full object-contain p-2"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Building2 className={`h-10 w-10 text-white ${rememberedCompany.logo ? 'hidden' : ''}`} />
                </div>
                
                <h2 className="text-xl font-bold text-slate-800 mb-1">
                  {rememberedCompany.name}
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  Tu última sesión fue aquí
                </p>

                <Button
                  onClick={handleQuickAccess}
                  className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold h-12 mb-3"
                  data-testid="quick-access-btn"
                >
                  <ArrowRight className="mr-2 h-5 w-5" />
                  Continuar a {rememberedCompany.name}
                </Button>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={handleChangeCompany}
                    className="flex-1 text-slate-600"
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Buscar otra empresa
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={handleForgetCompany}
                    className="text-slate-400 hover:text-red-500"
                    title="Olvidar esta empresa"
                  >
                    <AlertCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search Card (si no hay acceso rápido o eligió buscar otra) */}
        {!showQuickAccess && (
        <Card className="shadow-2xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mb-4">
              <Search className="h-8 w-8 text-white" />
            </div>
            <CardTitle>Buscar mi empresa</CardTitle>
            <CardDescription>
              Ingresa el nombre de tu empresa o tu correo electrónico
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSearch} className="space-y-4">
              <div>
                <Label htmlFor="search">Nombre de empresa o correo</Label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setError("");
                      setSearched(false);
                    }}
                    placeholder="Ej: Mi Empresa S.A. o correo@empresa.com"
                    className="pl-10"
                    autoFocus
                  />
                </div>
              </div>
              
              {error && (
                <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded-lg">
                  <AlertCircle className="h-4 w-4" />
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                disabled={loading}
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Buscar empresa
              </Button>
            </form>

            {/* Search Results */}
            {searched && searchResults.length > 0 && (
              <div className="mt-6 space-y-3">
                <Label className="text-sm text-slate-500">
                  {searchResults.length} empresa{searchResults.length > 1 ? "s" : ""} encontrada{searchResults.length > 1 ? "s" : ""}:
                </Label>
                {searchResults.map((company) => (
                  <div
                    key={company.id}
                    onClick={() => handleSelectCompany(company)}
                    className="flex items-center gap-4 p-4 bg-slate-50 hover:bg-blue-50 rounded-lg border border-slate-200 hover:border-blue-300 cursor-pointer transition-all group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center text-white font-bold">
                      {company.business_name?.charAt(0) || "E"}
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800 group-hover:text-blue-700">
                        {company.business_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {company.email || `Accede en /empresa/${company.slug}`}
                      </p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                  </div>
                ))}
              </div>
            )}

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-3 text-slate-500">o</span>
              </div>
            </div>

            {/* Super Admin Access */}
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate("/admin-portal")}
            >
              <Shield className="h-4 w-4 mr-2" />
              Acceso Super Administrador
            </Button>

            {/* Help text */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800">
                <strong>¿No encuentras tu empresa?</strong>
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Si tu empresa ya está registrada y no aparece, verifica que estés 
                escribiendo el nombre correctamente o contacta a tu administrador.
              </p>
            </div>

            {/* Back to home */}
            <div className="mt-6 text-center">
              <Link 
                to="/" 
                className="inline-flex items-center text-sm text-slate-500 hover:text-blue-600"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Volver al inicio
              </Link>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Footer */}
        <p className="text-center text-blue-200/60 text-sm mt-6">
          ¿No tienes cuenta?{" "}
          <Link to="/registro" className="text-blue-300 hover:text-white underline">
            Regístrate gratis
          </Link>
        </p>
      </div>
    </div>
  );
};

export default FindCompany;
